import { useState, useEffect, useCallback } from 'react';
import socketService from '@/services/socket';

interface TrackingData {
  latitude: number;
  longitude: number;
  speed: number;
  heading: number;
  lastUpdated: string;
  isOnline: boolean;
}

interface LiveTrackingHook {
  trackingData: TrackingData | null;
  isConnected: boolean;
  subscribeToOrder: (orderId: string) => void;
  unsubscribeFromOrder: (orderId: string) => void;
  subscribeToRider: (riderId: string) => void;
  unsubscribeFromRider: (riderId: string) => void;
}

export const useLiveTracking = (): LiveTrackingHook => {
  const [trackingData, setTrackingData] = useState<TrackingData | null>(null);
  const [isConnected, setIsConnected] = useState(false);

  useEffect(() => {
    // Wait for socket to be available
    const checkSocket = () => {
      const socket = socketService.getSocket();
      
      if (!socket) {
        console.log('Socket not available yet, retrying in 1 second...');
        setTimeout(checkSocket, 1000);
        return;
      }
      
      console.log('Socket available for live tracking:', socket.id);
      setupSocketListeners(socket);
    };
    
    checkSocket();
  }, []);

  const setupSocketListeners = (socket: Socket) => {

    // Connection status
    const handleConnect = () => {
      console.log('Live tracking: Socket connected');
      setIsConnected(true);
    };

    const handleDisconnect = () => {
      console.log('Live tracking: Socket disconnected');
      setIsConnected(false);
    };

    // Location updates
    const handleLocationUpdate = (data: any) => {
      console.log('Live tracking: Location update received', data);
      
      setTrackingData({
        latitude: data.latitude,
        longitude: data.longitude,
        speed: data.speed || 0,
        heading: data.heading || 0,
        lastUpdated: data.timestamp || new Date().toISOString(),
        isOnline: true
      });
    };

    // Join tracking room
    const handleJoinedTracking = (data: any) => {
      console.log('Live tracking: Joined tracking room', data);
    };

    // Leave tracking room
    const handleLeftTracking = (data: any) => {
      console.log('Live tracking: Left tracking room', data);
    };

    // Register event listeners
    socket.on('connect', handleConnect);
    socket.on('disconnect', handleDisconnect);
    socket.on('location:update', handleLocationUpdate);
    socket.on('tracking:joined', handleJoinedTracking);
    socket.on('tracking:left', handleLeftTracking);

    // Check initial connection status
    setIsConnected(socket.connected);

    // Return cleanup function
    return () => {
      socket.off('connect', handleConnect);
      socket.off('disconnect', handleDisconnect);
      socket.off('location:update', handleLocationUpdate);
      socket.off('tracking:joined', handleJoinedTracking);
      socket.off('tracking:left', handleLeftTracking);
    };
  };

  const subscribeToOrder = useCallback((orderId: string) => {
    const socket = socketService.getSocket();
    if (socket) {
      console.log('Live tracking: Subscribing to order', orderId);
      socket.emit('join:order-tracking', { orderId });
      socketService.joinRoom(`order:${orderId}`);
    }
  }, []);

  const unsubscribeFromOrder = useCallback((orderId: string) => {
    const socket = socketService.getSocket();
    if (socket) {
      console.log('Live tracking: Unsubscribing from order', orderId);
      socket.emit('leave:order-tracking', { orderId });
      socketService.leaveRoom(`order:${orderId}`);
    }
  }, []);

  const subscribeToRider = useCallback((riderId: string) => {
    const socket = socketService.getSocket();
    if (socket) {
      console.log('Live tracking: Subscribing to rider', riderId);
      socket.emit('join:rider-tracking', { riderId });
      socketService.joinRoom(`rider:${riderId}`);
    }
  }, []);

  const unsubscribeFromRider = useCallback((riderId: string) => {
    const socket = socketService.getSocket();
    if (socket) {
      console.log('Live tracking: Unsubscribing from rider', riderId);
      socket.emit('leave:rider-tracking', { riderId });
      socketService.leaveRoom(`rider:${riderId}`);
    }
  }, []);

  return {
    trackingData,
    isConnected,
    subscribeToOrder,
    unsubscribeFromOrder,
    subscribeToRider,
    unsubscribeFromRider
  };
};
